import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  FileText,
  Layers,
  Loader2,
  Lock,
  LogOut,
  Monitor,
  MoreVertical,
  Package,
  Plus,
  Printer,
  RotateCcw,
  Wrench,
  X,
} from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { useShiftBarActionsStore } from '@/store/shiftBarActionsStore';

export interface PosMobileOverflowMenuProps {
  canProcessReturns: boolean;
  userCanAddExpenses: boolean;
  showTodaySales: boolean;
  showCustomerDisplay: boolean;
  sellOnlyProducts: boolean;
  viewMode: 'products' | 'services';
  pendingCount: number;
  heldCount?: number;
  onReturn: () => void;
  onExpense: () => void;
  onPending: () => void;
  onTodaySales: () => void;
  onCustomerDisplay: () => void;
  onViewModeChange: (mode: 'products' | 'services') => void;
  onHeldCarts?: () => void;
  onGeneralItem: () => void;
}

/**
 * Mobile overflow actions — opens as a bottom sheet so it never clips off-screen
 * (especially in RTL where absolute dropdowns hide under the edge).
 */
export default function PosMobileOverflowMenu({
  canProcessReturns,
  userCanAddExpenses,
  showTodaySales,
  showCustomerDisplay,
  sellOnlyProducts,
  viewMode,
  pendingCount,
  heldCount = 0,
  onReturn,
  onExpense,
  onPending,
  onTodaySales,
  onCustomerDisplay,
  onViewModeChange,
  onHeldCarts,
  onGeneralItem,
}: PosMobileOverflowMenuProps) {
  const { t } = useTranslation();
  const shiftActions = useShiftBarActionsStore((s) => s.actions);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  const itemClass =
    'w-full text-start px-4 py-3.5 text-sm font-medium flex items-center gap-3 min-h-[48px] active:bg-gray-50';

  const sheet = open
    ? createPortal(
        <div className="fixed inset-0 z-[80] lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label={t('common.cancel')}
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85dvh] rounded-t-2xl bg-white shadow-2xl flex flex-col pb-[env(safe-area-inset-bottom)] animate-fade-in">
            <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-gray-300" aria-hidden />
            <div className="flex items-center justify-between px-4 pt-1 pb-2 border-b border-gray-100 shrink-0">
              <p className="text-sm font-semibold text-gray-900">{t('common.more', 'More')}</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={t('common.cancel')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain py-1">
              {!sellOnlyProducts && (
                <>
                  <button
                    type="button"
                    onClick={() => run(() => onViewModeChange('products'))}
                    className={`${itemClass} ${viewMode === 'products' ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                  >
                    <Package className="w-4 h-4 shrink-0" />
                    {t('pos.products')}
                  </button>
                  <button
                    type="button"
                    onClick={() => run(() => onViewModeChange('services'))}
                    className={`${itemClass} ${viewMode === 'services' ? 'bg-primary-50 text-primary-700' : 'text-gray-700'}`}
                  >
                    <Wrench className="w-4 h-4 shrink-0" />
                    {t('pos.services')}
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                </>
              )}
              <button
                type="button"
                onClick={() => run(onGeneralItem)}
                className={`${itemClass} text-gray-700`}
              >
                <Plus className="w-4 h-4 shrink-0" />
                {t('pos.generalItem')}
              </button>
              {heldCount > 0 && onHeldCarts && (
                <button
                  type="button"
                  onClick={() => run(onHeldCarts)}
                  className={`${itemClass} text-amber-900`}
                >
                  <Layers className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{t('pos.heldCarts')}</span>
                  <span className="bg-amber-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                    {heldCount}
                  </span>
                </button>
              )}
              {canProcessReturns && (
                <button type="button" onClick={() => run(onReturn)} className={`${itemClass} text-gray-700`}>
                  <RotateCcw className="w-4 h-4 shrink-0" />
                  {t('pos.return')}
                </button>
              )}
              {userCanAddExpenses && (
                <button type="button" onClick={() => run(onExpense)} className={`${itemClass} text-gray-700`}>
                  <PakRupeeIcon className="w-4 h-4 shrink-0" />
                  {t('pos.expense')}
                </button>
              )}
              <button type="button" onClick={() => run(onPending)} className={`${itemClass} text-gray-700`}>
                <Clock className="w-4 h-4 shrink-0" />
                <span className="flex-1">{t('pos.pending')}</span>
                {pendingCount > 0 && (
                  <span className="bg-amber-600 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                )}
              </button>
              {showTodaySales && (
                <button type="button" onClick={() => run(onTodaySales)} className={`${itemClass} text-gray-700`}>
                  <FileText className="w-4 h-4 shrink-0" />
                  {t('pos.todaySales')}
                </button>
              )}
              {showCustomerDisplay && (
                <button type="button" onClick={() => run(onCustomerDisplay)} className={`${itemClass} text-gray-700`}>
                  <Monitor className="w-4 h-4 shrink-0" />
                  {t('pos.customerDisplay')}
                </button>
              )}
              {shiftActions?.shiftOpen && (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  <button type="button" onClick={() => run(shiftActions.openPaidIn)} className={`${itemClass} text-gray-700`}>
                    <Plus className="w-4 h-4 shrink-0 text-emerald-700" />
                    {t('shift.paidIn')}
                  </button>
                  <button type="button" onClick={() => run(shiftActions.openPaidOut)} className={`${itemClass} text-gray-700`}>
                    <PakRupeeIcon className="w-4 h-4 shrink-0 text-orange-700" />
                    {t('shift.paidOut')}
                  </button>
                  {shiftActions.userCanAddExpenses && (
                    <>
                      <button type="button" onClick={() => run(shiftActions.openDrawerExpense)} className={`${itemClass} text-gray-700`}>
                        <Plus className="w-4 h-4 shrink-0" />
                        {t('shift.addDrawerExpense')}
                      </button>
                      <button type="button" onClick={() => run(shiftActions.openTodayExpenses)} className={`${itemClass} text-gray-700`}>
                        <PakRupeeIcon className="w-4 h-4 shrink-0" />
                        {t('shift.todaysExpenses')}
                      </button>
                    </>
                  )}
                  <button type="button" onClick={() => run(shiftActions.lockScreen)} className={`${itemClass} text-gray-700`}>
                    <Lock className="w-4 h-4 shrink-0" />
                    {t('shift.lockScreen')}
                  </button>
                  <button
                    type="button"
                    onClick={() => run(shiftActions.printX)}
                    disabled={shiftActions.printingX}
                    className={`${itemClass} text-gray-700 disabled:opacity-60`}
                  >
                    {shiftActions.printingX ? (
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    ) : (
                      <Printer className="w-4 h-4 shrink-0" />
                    )}
                    {t('xReport.printX')}
                  </button>
                  <button type="button" onClick={() => run(shiftActions.endShift)} className={`${itemClass} text-red-700`}>
                    <LogOut className="w-4 h-4 shrink-0" />
                    {t('shift.endShift')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="shrink-0 p-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center relative"
        aria-label={t('common.more', 'More')}
        aria-expanded={open}
      >
        <MoreVertical className="w-5 h-5" />
        {(pendingCount > 0 || heldCount > 0) && (
          <span className="absolute top-1 end-1 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white" />
        )}
      </button>
      {sheet}
    </>
  );
}
